import { escapeHtml } from '../inventoree/format'
import { lastActivityHtml } from '../inventoree/columns'

const DASH = '<i class="fas fa-minus font-10"></i>'

export interface DeviceRow {
  deviceId: number
  DT_RowId: string
  Tip: number
  device_type: string
  device_icon: string
  Denumire: string
  ownerId: number
  Prenume: string | null
  Nume: string | null
  UserLogat: string | null
  DeviceGroup: string | null
  installed_applications: number | string | null
  operating_system: string | null
  os_available_updates: number | string | null
  default_browser: string | null
  default_mail_client: string | null
  antivirus: string | null
  office: string | null
  os_version: string | null
  DataAccesareDM: string | null
  server_role?: { label?: string; matched?: string } | null
  server_service?: {
    status?: string
    role?: { label?: string; matched?: string }
    health?: { summary?: string }
    reasons?: { text: string }[]
    next_action?: { text?: string }
    updates?: { count?: number; security?: number; detail?: string; cve_ids?: string[] }
    upgrade?: { available?: string; current?: string; reason?: string; importance?: string; label?: string }
    primary?: { name?: string; version?: string }
  } | null
  __checked?: boolean
  [key: string]: unknown
}

/**
 * O coloana de lista Softsee. Aceleasi campuri ca in celelalte module
 * (`columns[i][name]` e coloana de ordonare pe server), dar randul e cel de
 * AICI: listele Softsee n-au `actions` si au coloanele de aplicatii, iar
 * randul lui Inventoree cerea campuri pe care serverul nu le trimite.
 */
export interface Column {
  index: number
  title: string
  titleHtml?: string
  name?: string
  orderable: boolean
  visible: boolean
  width?: number
  html(row: DeviceRow): string
}

function textOrDash(value: unknown): string {
  return value ? escapeHtml(value) : DASH
}

function nameCell(row: DeviceRow): string {
  const name = `<p>${escapeHtml(row.Denumire)}</p>`
  if (row.ownerId > 0) {
    return name + `<span class="color-light-grey"><i class="fa fa-user"></i> ${escapeHtml(row.Prenume)} ${escapeHtml(row.Nume)}</span>`
  }
  return name + '<span class="color-red"><i class="fa fa-user"></i> Unassigned</span>'
}

/** SoftseeDevicesModel + OdLastActivity. */
export function deviceColumns(): Column[] {
  return [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${r.deviceId}" data-type="${r.Tip}"><i class="fas fa-plus-circle"></i></center>` },
    { index: 1, title: 'Type', name: 'Tip', orderable: true, visible: true, width: 100, html: (r) => `<center style="cursor:default" data-toggle="tooltip" data-placement="top" title="${escapeHtml(r.device_type)}">${r.device_icon}</center>` },
    { index: 2, title: 'Device name', name: 'Denumire', orderable: true, visible: true, html: (r) => nameCell(r) },
    { index: 3, title: 'Last User Logged On', name: 'UserLogat', orderable: true, visible: true, html: (r) => textOrDash(r.UserLogat) },
    { index: 4, title: 'Group', name: 'DeviceGroup', orderable: true, visible: true, html: (r) => textOrDash(r.DeviceGroup) },
    { index: 5, title: 'Installed Applications', name: 'installed_applications', orderable: true, visible: true, html: (r) => (r.installed_applications ? `<span class="label bg-quartz">${escapeHtml(r.installed_applications)}</span>` : DASH) },
    { index: 6, title: 'Operating system', name: 'operating_system', orderable: true, visible: true, html: (r) => textOrDash(r.operating_system) },
    { index: 7, title: 'OS Available Updates', name: 'os_available_updates', orderable: true, visible: true, html: (r) => (r.os_available_updates ? `<span class="label bg-red-sunglo">${escapeHtml(r.os_available_updates)}</span>` : DASH) },
    { index: 8, title: 'Default browser', name: 'default_browser', orderable: true, visible: true, html: (r) => textOrDash(r.default_browser) },
    { index: 9, title: 'Email client', name: 'default_mail_client', orderable: true, visible: true, html: (r) => textOrDash(r.default_mail_client) },
    { index: 10, title: 'Antivirus', name: 'antivirus', orderable: true, visible: true, html: (r) => textOrDash(r.antivirus) },
    { index: 11, title: 'Office application', name: 'office', orderable: true, visible: true, html: (r) => textOrDash(r.office) },
    { index: 12, title: '', titleHtml: '<center><input type="checkbox" class="allDevices"/></center>', name: 'ID', orderable: false, visible: true, width: 50, html: (r) => `<center><input value="${r.deviceId}" type="checkbox" class="oneDevice"${r.__checked ? ' checked' : ''}/></center>` },
    { index: 13, title: 'Last Activity', name: 'DataAccesareDM', orderable: true, visible: true, html: (r) => lastActivityHtml(r.DataAccesareDM) },
  ]
}

function statusBadge(service: DeviceRow['server_service']): string {
  if (!service?.status) return DASH
  const map: Record<string, { cls: string; text: string }> = {
    healthy: { cls: 'label-success', text: 'Healthy' },
    degraded: { cls: 'label-warning', text: 'Degraded' },
    down: { cls: 'label-danger', text: 'Down' },
    unknown: { cls: 'label-default', text: 'Unknown' },
  }
  const m = map[service.status] ?? map.unknown
  let tip = service.health?.summary ?? service.status
  if (service.reasons?.length) tip = service.reasons.map((x) => x.text).join('; ') + (tip ? ' — ' + tip : '')
  if (service.next_action?.text) tip = service.next_action.text + (tip ? ' — ' + tip : '')
  return `<span class="label ${m.cls}" data-toggle="tooltip" data-placement="top" title="${escapeHtml(tip)}">${m.text}</span>`
}

function updatesBadge(row: DeviceRow): string {
  const service = row.server_service
  const upgrade = service?.upgrade
  const count = service?.updates ? parseInt(String(service.updates.count ?? 0), 10) : 0
  const security = service?.updates ? parseInt(String(service.updates.security ?? 0), 10) : 0
  if (upgrade && upgrade.importance && upgrade.importance !== 'none') {
    const text = upgrade.current && upgrade.available ? `${upgrade.current} → ${upgrade.available}` : upgrade.available ? `→ ${upgrade.available}` : upgrade.current ?? upgrade.label ?? upgrade.importance
    const cls = upgrade.importance === 'critical' || upgrade.importance === 'high' ? 'bg-red-sunglo' : upgrade.importance === 'medium' ? 'bg-warning' : 'bg-quartz'
    return `<span class="label ${cls}">${escapeHtml(text + ' · ' + (upgrade.label ?? upgrade.importance))}</span>`
  }
  if (count > 0 || security > 0) {
    const cls = security > 0 ? 'bg-red-sunglo' : 'bg-warning'
    const text = security > 0 ? `${security} security` : `${count} update${count === 1 ? '' : 's'}`
    return `<span class="label ${cls}">${escapeHtml(text)}</span>`
  }
  if (service?.primary?.version) {
    return `<span class="label bg-quartz">${escapeHtml(service.primary.version)}</span>`
  }
  if (row.os_available_updates && Number(row.os_available_updates) > 0) {
    return `<span class="label bg-red-sunglo">${escapeHtml(row.os_available_updates)}</span>`
  }
  return DASH
}

/** SoftseeServersModel + OdLastActivity. */
export function serverColumns(): Column[] {
  return [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${r.deviceId}" data-type="${r.Tip}"><i class="fas fa-plus-circle"></i></center>` },
    { index: 1, title: 'Type', name: 'Tip', orderable: true, visible: true, width: 100, html: (r) => `<center style="cursor:default" data-toggle="tooltip" data-placement="top" title="${escapeHtml(r.device_type)}">${r.device_icon}</center>` },
    { index: 2, title: 'Device name', name: 'Denumire', orderable: true, visible: true, html: (r) => nameCell(r) },
    { index: 3, title: 'Role', orderable: false, visible: true, html: (r) => {
      const role = r.server_service?.role ?? r.server_role
      if (!role?.label) return DASH
      return `<span class="label label-primary" data-toggle="tooltip" data-placement="top" title="${escapeHtml('runs ' + (role.matched ?? ''))}">${escapeHtml(role.label)}</span>`
    } },
    { index: 4, title: 'Status', orderable: false, visible: true, html: (r) => statusBadge(r.server_service) },
    { index: 5, title: 'Updates', orderable: false, visible: true, html: (r) => updatesBadge(r) },
    { index: 6, title: 'Applications', name: 'installed_applications', orderable: true, visible: true, html: (r) => (r.installed_applications ? `<span class="label bg-quartz">${escapeHtml(r.installed_applications)}</span>` : DASH) },
    { index: 7, title: 'Version', name: 'os_version', orderable: true, visible: true, html: (r) => textOrDash(r.os_version) },
    { index: 8, title: 'Operating system', name: 'operating_system', orderable: true, visible: true, html: (r) => textOrDash(r.operating_system) },
    { index: 9, title: 'Antivirus', name: 'antivirus', orderable: true, visible: true, html: (r) => textOrDash(r.antivirus) },
    { index: 10, title: 'Group', name: 'DeviceGroup', orderable: true, visible: true, html: (r) => textOrDash(r.DeviceGroup) },
    { index: 11, title: '', orderable: false, visible: true, width: 30, html: () => '' },
    { index: 12, title: 'Last Activity', name: 'DataAccesareDM', orderable: true, visible: true, html: (r) => lastActivityHtml(r.DataAccesareDM) },
  ]
}

/** ApplicationsModel */
export function applicationColumns(): Column[] {
  return [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${r.ID}"><i class="fas fa-plus-circle"></i></center>` },
    { index: 1, title: 'Application Name', name: 'short_name', orderable: true, visible: true, html: (r) => `<span class="far fa-save"></span> ${escapeHtml(r.short_name)}` },
    { index: 2, title: 'Last Version', name: 'last_version', orderable: true, visible: true, html: (r) => (r.last_version ? escapeHtml(r.last_version) : '') },
    { index: 3, title: 'Devices', name: 'devices', orderable: true, visible: true, html: (r) => (r.devices ? `<span class="badge bg-blue">${escapeHtml(r.devices)} devices</span>` : '') },
    { index: 4, title: 'Category', name: 'category_name', orderable: true, visible: true, html: (r) => (r.category_name ? escapeHtml(r.category_name) : '<em class="color-light-grey">no category</em>') },
  ]
}

/** SoftwareLicensesModel */
export function licenseColumns(): Column[] {
  return [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${r.ID}"><i class="fas fa-plus-circle"></i></center>` },
    { index: 1, title: 'Application Name', name: 'short_name', orderable: true, visible: true, html: (r) => escapeHtml(r.short_name) },
    { index: 2, title: 'Total Licenses', name: 'total', orderable: true, visible: true, html: (r) => (r.total ? `<i class="fa fa-star color-warning"></i> ${escapeHtml(r.total)}` : '<em>no license</em>') },
    { index: 3, title: 'Active Licenses', name: 'active', orderable: true, visible: true, html: (r) => (r.active ? `<i class="fa fa-star color-green3"></i> ${escapeHtml(r.active)}` : '<em>no license</em>') },
    { index: 4, title: 'Devices without license', name: 'devices', orderable: true, visible: true, html: (r) => {
      const devices = Number(r.devices ?? 0)
      const licensed = Number(r.devices_licensed ?? 0)
      return devices ? `<span class="badge bg-red-sunglo">${devices - licensed}</span> devices` : ''
    } },
    { index: 5, title: 'Last Version', name: 'last_version', orderable: true, visible: true, html: (r) => textOrDash(r.last_version) },
    { index: 6, title: 'Category', name: 'category_name', orderable: true, visible: true, html: (r) => textOrDash(r.category_name) },
  ]
}
